import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Breadcrumb from '../../../../components/bootstrap/Breadcrumb';
import Button from '../../../../components/bootstrap/Button';
import Card, { CardBody, CardHeader, CardLabel, CardTitle } from '../../../../components/bootstrap/Card';
import TableCustom from '../../../../components/MyCustom/TableCustom';
import SelectEstablishment from '../../../../components/MyCustom/SelectEstablishment';
import ModalViewAnswers from '../../../../components/modals/Feedbacks/ModalViewAnswers';
import { DefaultContext } from '../../../../contexts/default';
import useDarkMode from '../../../../hooks/useDarkMode';
import Page from '../../../../layout/Page/Page';
import PageWrapper from '../../../../layout/PageWrapper/PageWrapper';
import SubHeader, { SubHeaderLeft } from '../../../../layout/SubHeader/SubHeader';
import { demoPages } from '../../../../menu';
import api from '../../../../services/api';
import masks from '../../../../helpers/utils/masks';

const formatDate = (value) => {
  if (!value) return 'N/I';

  const toDateTimeLabel = (dateValue) => {
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return 'N/I';

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${day}/${month} ${hours}:${minutes}`;
  };

  if (value?.toDate) {
    return toDateTimeLabel(value.toDate());
  }

  if (value?.seconds) {
    return toDateTimeLabel(value.seconds * 1000);
  }

  if (value?._seconds) {
    return toDateTimeLabel(value._seconds * 1000);
  }

  if (typeof value === 'number') {
    return toDateTimeLabel(value);
  }

  return toDateTimeLabel(value);
};

const getCustomerName = (item, answers) => {
  const directName =
    item?.respondent_name ||
    item?.name ||
    item?.customer_name ||
    item?.customerName ||
    item?.client_name ||
    item?.clientName ||
    item?.customer?.name ||
    item?.client?.name;

  if (directName) return directName;

  const nameAnswer = answers.find((answer) => {
    const questionLabel =
      answer?.question_label ||
      answer?.label ||
      answer?.question ||
      answer?.name ||
      '';

    return /nome/i.test(questionLabel);
  });

  if (nameAnswer?.value) return nameAnswer.value;

  return 'N/I';
};

const getCustomerPhone = (item) => {
  const phone =
    item?.respondent_phone ||
    item?.phone ||
    item?.customer_phone ||
    item?.customerPhone ||
    item?.client_phone ||
    item?.clientPhone ||
    item?.customer?.phone ||
    item?.client?.phone ||
    '';

  if (!phone) return 'N/I';

  return masks.phone(String(phone).replace(/\D/g, ''));
};

const getCreatedAtLabel = (item) => {
  return formatDate(
    item?.created_at ||
    item?.createdAt ||
    item?.date ||
    item?.answered_at ||
    item?.answeredAt ||
    item?.submitted_at ||
    item?.submittedAt ||
    item?.updated_at ||
    item?.updatedAt,
  );
};

const normalizeAnswers = (data) => {
  const list = Array.isArray(data)
    ? data
    : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data?.data)
        ? data.data
        : [];

  return list.map((item, index) => {
    const answers = Array.isArray(item?.items)
      ? item.items
      : Array.isArray(item?.answers)
        ? item.answers
        : Array.isArray(item?.responses)
          ? item.responses
          : [];

    return {
      ...item,
      id: item?.id || `feedback-${index}`,
      customerName: getCustomerName(item, answers),
      customerPhone: getCustomerPhone(item),
      createdAtLabel: getCreatedAtLabel(item),
      questionCount: answers.length,
      items: answers,
    };
  });
};

const Feedbacks = () => {
  const { darkModeStatus } = useDarkMode();
  const { estabSelected, user } = useContext(DefaultContext);
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [feedbackSelected, setFeedbackSelected] = useState(null);
  const [openModalAnswers, setOpenModalAnswers] = useState(false);

  const loadFeedbacks = useCallback(async () => {
    if (!estabSelected) {
      setFeedbacks([]);
      return;
    }

    try {
      setLoading(true);
      const response = await api.get('routes/evaluation/answers', {
        params: { estabId: estabSelected },
      });

      const normalized = normalizeAnswers(response?.data).sort((a, b) => {
        const dateA = new Date(a.created_at || a.createdAt || a.date || 0).getTime();
        const dateB = new Date(b.created_at || b.createdAt || b.date || 0).getTime();
        return dateB - dateA;
      });

      setFeedbacks(normalized);
    } catch (error) {
      setFeedbacks([]);
    } finally {
      setLoading(false);
    }
  }, [estabSelected]);

  useEffect(() => {
    loadFeedbacks();
  }, [loadFeedbacks]);

  const handleOpenModalAnswers = useCallback((item) => {
    setFeedbackSelected(item);
    setOpenModalAnswers(true);
  }, []);

  const handleCloseModalAnswers = useCallback(() => {
    setFeedbackSelected(null);
    setOpenModalAnswers(false);
  }, []);

  const columns = useMemo(() => ([
    {
      label: 'Nome do cliente',
      field: 'customerName',
    },
    {
      label: 'Data',
      field: 'createdAtLabel',
    },
    {
      label: 'WhatsApp',
      field: 'customerPhone',
    },
    {
      label: 'Total de respostas',
      field: 'questionCount',
    },
    {
      label: 'Ações',
      field: 'actions',
      format: (row) => (
        <Button
          color={darkModeStatus ? 'light' : 'dark'}
          icon='Visibility'
          shadow='sm'
          hoverShadow='sm'
          size='sm'
          onClick={() => handleOpenModalAnswers(row)}>
          Ver respostas
        </Button>
      ),
      noExport: true,
    },
  ]), [darkModeStatus, handleOpenModalAnswers]);

  return (
    <>
      <PageWrapper title={demoPages.pdvMenu.subMenu.feedbacks.text}>
        <SubHeader>
          <SubHeaderLeft>
            <Breadcrumb
              list={[
                { title: 'PDV', to: '/pdv/products' },
                { title: 'Feedbacks', to: '/pdv/feedbacks' },
              ]}
            />
          </SubHeaderLeft>
        </SubHeader>

        <Page container='fluid'>
          <Card>
            <CardHeader>
              <CardLabel icon='QuestionAnswer' iconColor={darkModeStatus ? 'light' : 'dark'}>
                <CardTitle>Feedbacks</CardTitle>
              </CardLabel>
            </CardHeader>
            <CardBody className='table-responsive p-5'>
              <div className='row'>
                {user.role === 'admin' ? <SelectEstablishment /> : <></>}
              </div>

              {loading ? (
                <div className='text-center py-5'>Carregando feedbacks...</div>
              ) : !estabSelected ? (
                <div className='text-center py-5 text-muted'>
                  Selecione um estabelecimento para visualizar os feedbacks
                </div>
              ) : feedbacks.length === 0 ? (
                <div className='text-center py-5 text-muted'>
                  Nenhum feedback encontrado
                </div>
              ) : (
                <TableCustom
                  columns={columns}
                  rows={feedbacks}
                  keyExtractor={(row) => row.id}
                  fileName='Feedbacks'
                />
              )}
            </CardBody>
          </Card>
        </Page>
      </PageWrapper>

      <ModalViewAnswers
        itemSelected={feedbackSelected}
        open={openModalAnswers}
        setIsOpen={setOpenModalAnswers}
        setIsClose={handleCloseModalAnswers}
      />
    </>
  );
};

export default Feedbacks;
